/**
 * Servicio de chat
 * Sprint 11 - Endpoints de conversaciones y mensajes
 */

import { apiClient } from './api';
import type {
    AddConversationParticipantsPayload,
    AddConversationParticipantsResponse,
    CreateConversationPayload,
    CreateConversationResponse,
    ListConversationsResponse,
    ListMessagesResponse,
    MarkConversationAsReadPayload,
    MarkConversationAsReadResponse,
    SendMessagePayload,
    SendMessageResponse,
    ShowConversationResponse,
    UpdateConversationPresencePayload,
    UpdateConversationPresenceResponse,
} from '../types/chat';

interface ListConversationsParams {
    per_page?: number;
    [key: string]: string | number | undefined;
}

interface ListMessagesParams {
    per_page?: number;
    page?: number;
    [key: string]: string | number | undefined;
}

function buildQuery(params: Record<string, string | number | undefined>): string {
    const entries = Object.entries(params).filter(([, value]) => value !== undefined);

    if (entries.length === 0) {
        return '';
    }

    return `?${entries
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
        .join('&')}`;
}

export async function createConversation(payload: CreateConversationPayload): Promise<CreateConversationResponse> {
    return apiClient.post<CreateConversationResponse>('/api/chat/conversations', payload);
}

export async function getConversations(params: ListConversationsParams = {}): Promise<ListConversationsResponse> {
    return apiClient.get<ListConversationsResponse>(`/api/chat/conversations${buildQuery(params)}`);
}

export async function getConversation(conversationId: number): Promise<ShowConversationResponse> {
    return apiClient.get<ShowConversationResponse>(`/api/chat/conversations/${conversationId}`);
}

export async function getConversationMessages(
    conversationId: number,
    params: ListMessagesParams = {}
): Promise<ListMessagesResponse> {
    return apiClient.get<ListMessagesResponse>(
        `/api/chat/conversations/${conversationId}/messages${buildQuery(params)}`
    );
}

export async function sendConversationMessage(
    conversationId: number,
    payload: SendMessagePayload
): Promise<SendMessageResponse> {
    return apiClient.post<SendMessageResponse>(
        `/api/chat/conversations/${conversationId}/messages`,
        payload
    );
}

export async function addConversationParticipants(
    conversationId: number,
    payload: AddConversationParticipantsPayload
): Promise<AddConversationParticipantsResponse> {
    return apiClient.post<AddConversationParticipantsResponse>(
        `/api/chat/conversations/${conversationId}/participants`,
        payload
    );
}

export async function updateConversationPresence(
    conversationId: number,
    payload: UpdateConversationPresencePayload
): Promise<UpdateConversationPresenceResponse> {
    return apiClient.post<UpdateConversationPresenceResponse>(
        `/api/chat/conversations/${conversationId}/presence`,
        payload
    );
}

export async function markConversationAsRead(
    conversationId: number,
    payload: MarkConversationAsReadPayload = {}
): Promise<MarkConversationAsReadResponse> {
    return apiClient.post<MarkConversationAsReadResponse>(
        `/api/chat/conversations/${conversationId}/read`,
        payload
    );
}

export const chatService = {
    createConversation,
    getConversations,
    getConversation,
    getConversationMessages,
    sendConversationMessage,
    addConversationParticipants,
    updateConversationPresence,
    markConversationAsRead,
};
